// Turn the [[slug]] links of a node's markdown into ordinary markdown links,
// so react-markdown renders them without a custom plugin.
//
// The rules are plan §3.1, and they have to match graph_parser.py exactly: the
// backend decides what IS an edge, and a page that linked something the sync
// ignored — or ignored something the sync linked — would be lying about the
// graph. Both skip fenced and inline code for the same reason: a node teaching
// this syntax must not link itself.

const SEGMENT = /(```[\s\S]*?```|`[^`\n]*`)/g;
const LINK = /\[\[([^\]|\n]+?)(?:\|([^\]\n]*))?\]\]/g;

/**
 * @param {string} markdown  the node body
 * @param {Map<string,string>} known  slug -> title for every node that exists;
 *   anything not in it is dangling. A Map rather than a Set so a bare
 *   [[slug]] can be shown under the target's real title — "System of linear
 *   equations" reads as prose where "system_of_linear_equations" reads as a
 *   filename, and the author should not have to write [[slug|text]] to get it.
 * @returns {string} markdown with [[slug]] replaced by links or dangling markers
 */
// A whole display equation written on one line — `$$ … $$` — which is how every
// note in this repository writes them, and what the house style's §6 shows.
const ONE_LINE_DISPLAY = /^[ \t]*\$\$[ \t]*(.+?)[ \t]*\$\$[ \t]*$/gm;

/**
 * Put single-line `$$ … $$` fences on their own lines.
 *
 * remark-math only treats `$$` as DISPLAY math when the fences open and close
 * their own lines; all on one line it parses as inline math instead. The
 * difference is not cosmetic — inline math breaks across lines at the nearest
 * atom, so a matrix equation wraps in the middle and `b = [-13, 9]` lands on a
 * line of its own. As display math it stays one object and scrolls, which is
 * what `.katex-display` in index.css is for.
 *
 * Rewriting the layout here rather than in the node files keeps the authoring
 * style the same as the rest of the repository.
 */
export function normalizeDisplayMath(markdown) {
  if (!markdown) return "";
  return markdown
    .split(SEGMENT)
    .map((part, i) =>
      i % 2 === 1 ? part : part.replace(ONE_LINE_DISPLAY, "$$$$\n$1\n$$$$"),
    )
    .join("");
}

// A markdown image alone on its line — the only shape §4.2 allows, since a
// figure "sits on its own" and is never inlined in a sentence.
const FIGURE_LINE = /^[ \t]*!\[([^\]]*)\]\(([^)\s]+)(?:\s+"[^"]*")?\)[ \t]*$/;
const EXISTING_CAPTION = /^[ \t]*\*Fig\./;

/**
 * Caption and number every figure `Fig. <node id>.<n>`, n counting from 1.
 *
 * House style §4.4: the caption is italic and goes BELOW the figure, with a
 * blank line between them — the opposite of a table, whose caption sits above.
 * Figures are the only thing a node numbers — display equations are not, since
 * nothing in a one-idea node has to refer back to one.
 *
 * The number has two fields, not the three of §4.4. That section numbers from
 * the enclosing heading, and a node document has no headings, so there is no
 * third field to fill and one that was always 0 would say nothing.
 *
 * The caption text is the image's ALT TEXT, which §4.2 already requires to say
 * what the figure shows. Writing it once means the caption and the alt text
 * cannot drift apart, and a figure that fails to load says the same thing as
 * one that renders.
 *
 * A figure already followed by its own `*Fig. …*` line is left alone: an author
 * who wrote a caption meant it, and numbering it again would produce two.
 */
export function captionFigures(markdown, id) {
  if (!markdown || id === undefined || id === null) return markdown || "";
  return markdown
    .split(SEGMENT)
    .map((part, i) => {
      if (i % 2 === 1) return part; // literal code
      const lines = part.split("\n");
      const out = [];
      let count = 0;
      for (let n = 0; n < lines.length; n += 1) {
        const line = lines[n];
        out.push(line);
        const match = FIGURE_LINE.exec(line);
        if (!match) continue;

        // Look past blank lines for a caption the author already wrote.
        let ahead = n + 1;
        while (ahead < lines.length && lines[ahead].trim() === "") ahead += 1;
        if (ahead < lines.length && EXISTING_CAPTION.test(lines[ahead])) continue;

        count += 1;
        const alt = (match[1] || "").trim();
        // No trailing period, and never an empty caption — a figure with no alt
        // text still gets its number so the prose can refer to it.
        const said = alt.replace(/\.$/, "");
        out.push("");
        out.push(said ? `*Fig. ${id}.${count}: ${said}*` : `*Fig. ${id}.${count}*`);
      }
      return out.join("\n");
    })
    .join("");
}

// Display equations are NOT numbered. A node is a single idea a paragraph long,
// so its prose never has to say "see (2.1)" — the equation is three lines above
// whatever refers to it. The tag was pure decoration, and one that made every
// node carry a number keyed to its database id. An equation an author tagged
// with `\tag{…}` by hand still renders its tag: that is content, not numbering.

export function renderWikiLinks(markdown, known) {
  if (!markdown) return "";
  // Split on code, keeping it: odd indices are code and are passed through.
  return markdown
    .split(SEGMENT)
    .map((part, i) => {
      if (i % 2 === 1) return part; // a code span or fence — literal
      return part.replace(LINK, (_match, slug, text) => {
        const target = slug.trim();
        // Explicit display text wins; then the target's title; then the slug.
        const title = known && known.get ? known.get(target) : null;
        const label = (text || title || target).trim();
        // A dangling link is a normal working state, so it renders as text with
        // a marker rather than as a broken link or an error (§3.1).
        if (known && !known.has(target)) return `${label} ⟨?⟩`;
        return `[${label}](/graph/node/${target})`;
      });
    })
    .join("");
}

export default renderWikiLinks;
